import { useContext, useEffect, useRef, useState } from 'react';
import { useAlert } from '../../../context/alertContext';
import { UserContext } from '../../../context/authContext';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { loadStripe, Stripe, StripeEmbeddedCheckout } from '@stripe/stripe-js';
import {
  primaryButtonGreen,
  primaryGreenDarker
} from '../../tokenStyle/tokeReset';
import { KoruLoading } from '../shared/KoruLoading';

const options = [
  { coins: 500, price: 30, priceId: 'price_1ST4PMCsMsu64ZEfZQo3fp5i' },
  { coins: 1000, price: 50, priceId: 'price_1SSievCsMsu64ZEfDmST59Eg' },
  { coins: 1500, price: 70, priceId: 'price_1SSihHCsMsu64ZEfLaEWUOyT' },
  { coins: 2000, price: 90, priceId: 'price_1SSii2CsMsu64ZEfjefGnVXn' }
];
// const pagesOptions = [
//   { pages: 1000, price: 25, priceId: 'price_1S4SnKCsMsu64ZEfV3YXxk16' }
// ];

export const AccountModal = () => {
  const { subsMainAlert, setSubsAlert, clearSubsAlert, setAlert } = useAlert();
  const { token, auth, companyAccount } = useContext(UserContext);
  // refs

  const containerRef = useRef<HTMLDivElement | null>(null);
  const checkoutRef = useRef<StripeEmbeddedCheckout | null>(null);
  const stripeRef = useRef<Stripe | null>(null);
  // state
  const [loading, setLoading] = useState<boolean>(false);
  const [showCoinsCheckout, setShowCoinsCheckout] = useState<boolean>(false);
  const [coinsPriceId, setCoinsPriceId] = useState<string>('');
  const [coinsCheckoutLoading, setCoinsCheckoutLoading] =
    useState<boolean>(false);
  // functions
  const fetchCoinsClientSecret = async () => {
    if (!companyAccount || !coinsPriceId) return;
    setCoinsCheckoutLoading(true);
    const res = await axios.post(
      `${process.env.REACT_APP_KORU_API}/checkoutsession`,
      {
        mode: 'payment', // or 'payment'
        priceId: coinsPriceId,
        successPath: `${process.env.REACT_APP_BASE_URL}/confirmation`,
        cancelPath: `${process.env.REACT_APP_BASE_URL}/billing/cancel`,
        quantity: 1,
        customerId: companyAccount?.stripeCustomerId,
        metadata: {
          app_customerId: companyAccount.customerId,
          payment_type: 'coins_purchase',
          priceId: coinsPriceId
        }
      },

      auth
    );
    console.log('res from checkout', res);
    const { clientSecret } = res.data;
    return clientSecret;
  };
  const handleChangePlan = async () => {
    if (!token) return;
    try {
      setLoading(true);
      let returnUrl;
      switch (true) {
        case subsMainAlert.alertType === 'coins':
          returnUrl = 'https://app.korucompliance.com/app/compliance-credits';
          break;
        case subsMainAlert.alertType === 'users':
          returnUrl = 'https://app.korucompliance.com/app/admin/users';
          break;
        case subsMainAlert.alertType === 'pages':
          returnUrl = 'https://app.korucompliance.com/app/compliance-credits';
          break;
        default:
          returnUrl = 'https://app.korucompliance.com/app/landing';
      }
      const result = await axios.post(
        `${process.env.REACT_APP_KORU_API}/stripe-portal`,
        {
          returnUrl
        },
        auth
      );
      const url = result.data.portalUrl;
      setLoading(false);
      window.location.href = url;
    } catch (e) {
      const error = e as Error;
      setLoading(false);
      setAlert(`${error.message}`, 'error');
    }
  };
  useEffect(() => {
    if (!showCoinsCheckout) return;

    let cancelled = false;

    (async () => {
      // 1) Load Stripe
      if (!stripeRef.current) {
        stripeRef.current = await loadStripe(
          process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!
        );
      }
      const stripe = stripeRef.current!;
      if (!stripe || !containerRef.current) return;

      // 2) Initialize embedded checkout with a fetcher + onComplete
      const checkout = await stripe.initEmbeddedCheckout({
        fetchClientSecret: fetchCoinsClientSecret,
        onComplete: () => {
          // 3) Destroy the checkout instance, then close the modal
          checkoutRef.current?.destroy(); // frees the iframe & listeners
          checkoutRef.current = null;
          clearSubsAlert();
        }
      });

      if (cancelled) {
        checkout.destroy();
        return;
      }

      checkoutRef.current = checkout;
      // 4) Mount into the modal body
      checkout.mount(containerRef.current);
      setCoinsCheckoutLoading(false);
    })();

    // Cleanup if the modal unmounts or closes early
    return () => {
      cancelled = true;
      checkoutRef.current?.destroy();
      checkoutRef.current = null;
      clearSubsAlert();
      setCoinsCheckoutLoading(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCoinsCheckout, coinsPriceId]);

  const buyMoreButtonStyle = `bg-[${primaryButtonGreen}] text-white px-4 py-2 rounded hover:bg-[${primaryGreenDarker}]`;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-800/40">
      {subsMainAlert.alertType === 'users' && (
        <div className="relative bg-white p-8 border rounded-xl">
          <p>You have reached the limit of users for this plan.</p>
          <div className="w-full mt-4 p-4 flex flex-row justify-around">
            <button
              onClick={() => clearSubsAlert()}
              className="inline-flex items-center justify-center rounded-lg border border-[#DA566B] px-4 py-2 text-sm font-medium text-[#B9495B] hover:bg-[#B9495B] hover:text-white focus:outline-none focus:ring-2 focus:ring-[#B9495B] disabled:bg-gray-300 disabled:text-gray-600 disabled:border-gray-600 disabled:ring-0"
            >
              cancel
            </button>
            <button
              onClick={() => handleChangePlan()}
              className="inline-flex items-center justify-center rounded-lg bg-[#56dac5] px-4 py-2 text-sm font-medium text-white hover:bg-[#2CC9B0] focus:outline-none focus:ring-2 focus:ring-[#2CC9B0]"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading
                </>
              ) : (
                <>Change Plan</>
              )}
            </button>
          </div>
        </div>
      )}
      {subsMainAlert.alertType === 'coins' && (
        <>
          {!showCoinsCheckout && (
            <div className="relative p-6 bg-white rounded-lg w-full max-w-md mx-auto">
              <button
                onClick={() => {
                  clearSubsAlert();
                  setShowCoinsCheckout(false);
                  setCoinsPriceId('');
                }}
                className="absolute top-2 right-2 text-slate-900 text-lg font-bold hover:text-gray-200"
              >
                ×
              </button>
              <h2 className="text-xl font-bold mb-4 text-center">
                Buy More Credits
              </h2>
              <p className="text-gray-600 text-center mb-4">
                These credits, unlike your monthly allocation, do not expire at
                the end of the month.
              </p>
              <div className="space-y-4">
                {options.map((option) => (
                  <div
                    key={option.coins}
                    className="border rounded-lg p-4 hover:shadow-lg transition cursor-pointer flex justify-between items-center"
                    // onClick={() => handlePurchase(option.coins, option.price)}
                  >
                    <div>
                      <p className="text-lg font-semibold">
                        {option.coins} Credits
                      </p>
                      <p className="text-gray-500">${option.price}</p>
                    </div>
                    <button
                      onClick={() => {
                        setCoinsPriceId(option.priceId);
                        setShowCoinsCheckout(true);
                      }}
                      className={buyMoreButtonStyle}
                    >
                      Buy
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {showCoinsCheckout && (
            <div
              className={`relative p-2 bg-[${primaryButtonGreen}] rounded-lg w-[75vw] mx-auto`}
            >
              <button
                onClick={() => {
                  clearSubsAlert();
                  setShowCoinsCheckout(false);
                  setCoinsPriceId('');
                }}
                className="absolute top-2 right-2 text-white text-lg font-bold hover:text-gray-200"
              >
                ×
              </button>
              <div
                ref={containerRef}
                className="p-2"
                style={{
                  display: !coinsCheckoutLoading ? 'block' : 'none'
                }}
              />
              {coinsCheckoutLoading && (
                <KoruLoading centerText="Loading Checkout" />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
